import ThumbCard from "@/features/home/VenuePreview/ThumbCard";
import { cn } from "@/lib/utils";
import { Link, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { bucketUrlPrefix, showNoticesTab } from "common/config";
import { VenueDetailsBySlugResponse, useAuth, useI18N } from "common/hooks";
import { useNearbyBusinesses } from "common/hooks/business/useNearbyBusinesses";
import ScrollContainer from "react-indiana-drag-scroll";
import BusinessReviews from "./BusinessReviews";
import NoReviews from "@/assets/images/noreviews.svg";
import Modal from "react-responsive-modal";
import { useEffect, useRef, useState, useMemo } from "react";
import { BusinessPhoto, VenueNotice, Notices } from "common/types";
import NoticesView from "@/features/home/VenuePreview/NoticesView";
import NoticesTopView from "@/features/home/VenuePreview/NoticesTopView";
import AccessibilitySection from "./AccessibilitySection";
import AccessibilitySectionHybrid from "./AccessibilitySectionHybrid";
import ScrollToTop from "react-scroll-to-top";
import { AddReviewModal } from "@/features/add-review/AddReviewModal";
import FlagVenueReview from "@/features/flag-report/FlagVenueReview";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
} from "@/store/atoms";
import { CaretDown } from "phosphor-react";
import { CirclePlus } from "lucide-react";
import AddMapperNote from "@/features/home/VenuePreview/AddMapperNote";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import AnalyticsService from "@/services/analytics";
import { useDarkMode } from "@/hooks/useDarkMode";
import AreaDetailsNotes from "@/features/home/VenuePreview/AreaDetailsNotes";
import AddVenueNote from "@/features/home/VenuePreview/AddVenueNote";
import FlagVenueNote from "@/features/flag-report/FlagVenueNote";
import VenueMapperNotes from "./VenueNotes/VenueMapperNotes";
import VenueNotes from "./VenueNotes/VenueNotes";
import MapperNotesComponent from "./VenueNotes/MapperNotes";
import { NearbyVenues } from "./NearbyVenues";
import Loader from "@/components/common/Loader";
import VenueInfoAddress from "@/features/home/VenuePreview/VenueInfoAddress";
import VenueInfoGoogleMap from "@/features/home/VenuePreview/VenueInfoGoogleMap";
import VenueInfoButtons from "@/features/home/VenuePreview/VenueInfoButtons";
import { RowsPhotoAlbum } from "react-photo-album";
import FullImageViewer from "@/components/FullImageViewer";
type VenueSheetContentProps = {
  onClose: () => void;
  venueDetailsReponse: VenueDetailsBySlugResponse;
  fullSize?: boolean;
  onRefresh: () => void;
};

// Define types for the photo data structure
interface PhotoItem {
  path: string | null;
  alt_text?: string | null;
  category_name: string | null;
  category_id: number | null;
  tag_name: string | null;
  created_at: string | null;
  photo_type?: string | null;
  review_photos_id?: string | null;
  width?: number | null;
  height?: number | null;
  sort_order?: number | null;
  // Venue type flag to determine rendering behavior
  venueType?: 'Basic' | 'Hybrid';
  // Accessibility tag properties
  qtags?: Array<{
    tagId: number | null;
    tagName: string | null;
    categoryDisplaySectionId?: string | null;
  }>;
  tags?: Array<{
    tagName: string | null;
    categoryDisplaySectionId?: string | null;
  }>;
  // Display sections for accessibility tags
  displaySections?: Record<string, string | null>;
}

function getFivePhotos(businessPhotos: BusinessPhoto[]): BusinessPhoto[] {
  const uniqueTagPhotosMap = new Map<string, BusinessPhoto>();
  const result: BusinessPhoto[] = [];

  // Populate the map with unique tagNames
  for (const photo of businessPhotos) {
    if (!uniqueTagPhotosMap.has(photo.tagName)) {
      uniqueTagPhotosMap.set(photo.tagName, photo);
    }
    if (uniqueTagPhotosMap.size >= 5) {
      break;
    }
  }

  // Add unique photos to the result
  const uniquePhotos = Array.from(uniqueTagPhotosMap.values());
  result.push(...uniquePhotos);

  // If we have fewer than 5 unique photos, add more photos from the original array without duplicating
  for (const photo of businessPhotos) {
    if (result.length >= 5) {
      break;
    }
    if (!result.includes(photo)) {
      result.push(photo);
    }
  }

  return result;
}

function VenueSheetContent({
  venueDetailsReponse,
  fullSize = false,
  onRefresh,
}: VenueSheetContentProps) {
  const i18n = useI18N();
  const venueDetails = venueDetailsReponse.details;
  const accessibilityCategories = venueDetailsReponse.accesibilityCategories;
  const accesibilityCategoriesHybrid =
    venueDetailsReponse.accesibilityCategoriesHybrid;

  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const [currentReviewSort, setCurrentReviewSort] = useState("Latest");
  const [reviews, setReviews] = useState(venueDetails.businessReviews);
  const [areaDetailsOpenModal, setAreaDetailsOpenModal] = useState(false);

  const accessibilitySectionRefs = useRef(null);
  const mapperNotesRef = useRef(null);

  const { showModal } = useAuthModals();

  const { isLoggedIn, user } = useAuth();
  const showMapperView =
    isLoggedIn &&
    (user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam);

  const [isAddReviewOpen, setIsAddReviewOpen] = useState(false);
  const [isFlagReviewOpen, setIsFlagReviewOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<any>(null);
  const [addNoteOpenModal, setAddNoteOpenModal] = useState(false);

  const [editVenueNoteOpenModal, setEditVenueNoteOpenModal] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [flagVenueNoteOpenModal, setFlagVenueNoteOpenModal] = useState(false);
  const [flagNote, setFlagNote] = useState(null);

  // Add nearby businesses query
  const { data: nearbyBusinessesData, isLoading: isLoadingNearbyBusinesses } =
    useNearbyBusinesses(venueDetails.business.id.toString());

  const [fullImageView, setFullImageView] = useState(false);
  const [currentImage, setCurrentImage] = useState<PhotoItem | null>(null);
  const [photoDimensions, setPhotoDimensions] = useState<{[key: string]: {width: number, height: number}}>({});
  

  // Add hasNearbyVenues constant
  const hasNearbyVenues = useMemo(() => {
    if (isLoadingNearbyBusinesses) return false;
    return (
      venueDetails.templateType !== "Basic" &&
      nearbyBusinessesData?.data?.nearbyBusinessCategories?.length > 0
    );
  }, [
    isLoadingNearbyBusinesses,
    venueDetails.templateType,
    nearbyBusinessesData,
  ]);

  const hasAccessibilityData =
    !hasNearbyVenues ||
    accessibilityCategories?.length > 0 ||
    accesibilityCategoriesHybrid?.length > 0;

  const isLoadingTabs = isLoadingNearbyBusinesses;

  const [showReviewsTab, setShowReviewsTab] = useState(false);
  const [showNotesTab, setShowNotesTab] = useState(false);
  
  const [selectedMainTab, setSelectedMainTab] = useState<
    "accessibility" | "nearbyvenues" | "reviews" | "mapperNotes" | "notices" | "sitemap" | "transportOptions"
  >("reviews"); // Start with reviews as temporary default

  // Update selected tab when loading completes
  useEffect(() => {
    if (!isLoadingTabs) {
      if (hasAccessibilityData) {
        setSelectedMainTab("accessibility");
      } else if (hasNearbyVenues) {
        setSelectedMainTab("nearbyvenues");
      }
    }
  }, [isLoadingTabs, hasAccessibilityData, hasNearbyVenues]);

  // Update selected tab if current selection becomes unavailable
  useEffect(() => {
    if (selectedMainTab === "accessibility" && !hasAccessibilityData) {
      setSelectedMainTab(hasNearbyVenues ? "nearbyvenues" : "reviews");
    } else if (selectedMainTab === "nearbyvenues" && !hasNearbyVenues) {
      setSelectedMainTab("reviews");
    }
  }, [hasAccessibilityData, hasNearbyVenues, selectedMainTab]);

  useEffect(() => {
    if (!hasAccessibilityData && !isLoadingTabs && hasNearbyVenues) {
      //set showNotesTab
      if(showMapperView && (venueDetails?.businessNotesForMappers?.length > 0 || venueDetails?.businessSociabilitySaysApproved?.length > 0)) {
        setShowNotesTab(true);
      } else if(!showMapperView && (venueDetails?.businessSociabilitySaysApproved?.length > 0)) {
        setShowNotesTab(true);
      }else{
        setShowNotesTab(false);
      }

      // set showReviewsTab
      if(venueDetails.businessReviews.length > 0) {
        setShowReviewsTab(true);
      } else {
        setShowReviewsTab(false);
      }

      //set default
    }else{
      setShowReviewsTab(true);
      setShowNotesTab(true);
    }
  }, [hasAccessibilityData, hasNearbyVenues, selectedMainTab, showMapperView]);

  const mainBaseTabs =
    showMapperView ||
    venueDetailsReponse.details.businessSociabilitySaysApproved.length > 0
      ? [
          ...(hasAccessibilityData ? ["accessibility"] : []),
          ...(hasNearbyVenues ? ["nearbyvenues"] : []),
          ...(showReviewsTab ? ["reviews"] : []),
          ...(showNotesTab ? ["mapperNotes"] : []),
          ...(venueDetails.business.siteMapPhoto ? ["sitemap"] : []),
          ...(venueDetails.business.transportOptionText ? ["transportOptions"] : []),
        ]
      : [
          ...(hasAccessibilityData ? ["accessibility"] : []),
          ...(hasNearbyVenues ? ["nearbyvenues"] : []),
          ...(showReviewsTab ? ["reviews"] : []),
          ...(venueDetails.business.siteMapPhoto ? ["sitemap"] : []),
          ...(venueDetails.business.transportOptionText ? ["transportOptions"] : []),
        ];

  const mainTabs =
    showNoticesTab === "Yes" ? [...mainBaseTabs, "notices"] : mainBaseTabs;

  const baseTabs = [
    ...(hasAccessibilityData ? ["Accessibility"] : []),
    ...(hasNearbyVenues ? ["Nearby Venues"] : []),
    ...(showReviewsTab ? ["Reviews"] : []),
    ...(showNotesTab ? ["Notes"] : []),
    ...(venueDetails.business.siteMapPhoto ? ["Sitemap"] : []),
    ...(venueDetails.business.transportOptionText ? ["Transport Options"] : []),
  ];
  const mainTabName =
    showNoticesTab === "Yes" ? [...baseTabs, "Notices"] : baseTabs;

  const [initialSection, setInitialSection] = useState("All");

  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom(actionToPerformValueAfterLoginAtom);

  const isDark = useDarkMode();

  // Map VenueNotice to Notices type
  const mapVenueNoticeToNotices = (notice: VenueNotice): Notices => ({
    ...notice,
    path_thumb: parseInt(notice.path_thumb) || 0,
  });

  // Merge pending and approved notes for mappers
  const mergedNotes = useMemo(() => {
    if (!showMapperView)
      return venueDetails.businessSociabilitySaysApproved || [];

    const approved = Array.isArray(venueDetails.businessSociabilitySaysApproved)
      ? venueDetails.businessSociabilitySaysApproved
      : [];
    const pending = Array.isArray(venueDetails.businessSociabilitySaysPending)
      ? venueDetails.businessSociabilitySaysPending
      : [];

    return [...approved, ...pending];
  }, [
    showMapperView,
    venueDetails.businessSociabilitySaysApproved,
    venueDetails.businessSociabilitySaysPending,
  ]);

  useEffect(() => {
    if (selectedMainTab === "reviews") {
      AnalyticsService.trackGAEventHandler("postReviewViewedReviewPage");
      AnalyticsService.trackMx("postReviewViewedReviewPage", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
      if (currentReviewSort === "Latest") {
        reviews.sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        setReviews([...reviews]);
      } else if (currentReviewSort === "Earliest") {
        reviews.sort(
          (a, b) =>
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        );
        setReviews([...reviews]);
      } else if (currentReviewSort === "Alphabetical") {
        reviews.sort((a, b) => {
          const nameA = a.notes.toUpperCase();
          const nameB = b.notes.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
        setReviews([...reviews]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReviewSort]);

  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "addReview") {
        setSelectedMainTab("reviews");
        setIsAddReviewOpen(true);
      } else if (actionToPerformAfterLogin === "flagReview") {
        setSelectedMainTab("reviews");
        setSelectedReview(actionToPerformValueAfterLogin);
        setIsFlagReviewOpen(true);
      }
      setActionToPerformAfterLogin("");
      setActionToPerformValueAfterLogin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  const top5Photos = getFivePhotos(venueDetails.businessPhotos);

  const handleShowLoginModal = () => {
    // Check if the current route matches the target route
    // if venue layer redirect to venue detail and then open login modal
    // if venue detail only then no redirect needed
    if (
      !matchRoute({
        to: `/venuedetails/${venueDetails.business.slug}`,
      })
    ) {
      navigate({
        to: `/venuedetails/${venueDetails.business.slug}`,
        search: { source: "map", showAuthModal: true } as any,
      });
    }

    showModal(AuthModalScreen.EnterEmail);
  };

  const handleAddReviewClose = () => {
    AnalyticsService.trackGAEventHandler("postReviewPostReviewExit");
    AnalyticsService.trackMx("postReviewPostReviewExit", {});
    setIsAddReviewOpen(false);
  };

  const handleFlagVenueReviewClose = () => {
    AnalyticsService.trackGAEventHandler("flagReviewReportClose");
    AnalyticsService.trackMx("flagReviewReportClose", {});
    setIsFlagReviewOpen(false);
  };

  const handleAddNoteModalClose = () => {
    AnalyticsService.trackGAEventHandler("mapperNoteReportClose");
    AnalyticsService.trackMx("mapperNoteReportClose", {});
    setAddNoteOpenModal(false);
  };

  const handleTabTracking = (selectedTab) => {
    console.log("selectedTab", selectedTab);
    if (selectedTab === "accessibility") {
      AnalyticsService.trackGAEventHandler(
        "navigatedToAccessibilityInformation"
      );
      AnalyticsService.trackMx("navigatedToAccessibilityInformation", {});
    } else if (selectedTab === "reviews") {
      AnalyticsService.trackGAEventHandler("navigatedToReviewsInformation");
      AnalyticsService.trackMx("navigatedToReviewsInformation", {});
    } else if (selectedTab === "mapperNotes") {
      AnalyticsService.trackGAEventHandler("navigatedToMapperNotesInformation");
      AnalyticsService.trackMx("navigatedToMapperNotesInformation", {});
    } else if (selectedTab === "notices") {
      AnalyticsService.trackGAEventHandler("navigatedToNoticesInformation");
      AnalyticsService.trackMx("navigatedToNoticesInformation", {});
    } else if (selectedTab === "nearbyvenues") {
      AnalyticsService.trackGAEventHandler(
        "navigatedToNearbyVenuesInformation"
      );
      AnalyticsService.trackMx("navigatedToNearbyVenuesInformation", {});
    } else if (selectedTab === "sitemap") {
      AnalyticsService.trackGAEventHandler("navigatedToSitemapInformation");
      AnalyticsService.trackMx("navigatedToSitemapInformation", {});
    } else if (selectedTab === "transportOptions") {
      AnalyticsService.trackGAEventHandler("navigatedToTransportOptionsInformation");
      AnalyticsService.trackMx("navigatedToTransportOptionsInformation", {});
    } 
  };

  const handleReviewSort = (value) => {
    console.log("handleReviewSort value", value);

    if (value === "Latest") {
      AnalyticsService.trackGAEventHandler("sortedReviewsByLatest");
      AnalyticsService.trackMx("sortedReviewsByLatest", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else if (value === "Earliest") {
      AnalyticsService.trackGAEventHandler("sortedReviewsByEarliest");
      AnalyticsService.trackMx("sortedReviewsByEarliest", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else {
      AnalyticsService.trackGAEventHandler("sortedReviewsByAlphabetical");
      AnalyticsService.trackMx("sortedReviewsByAlphabetical", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    }

    setCurrentReviewSort(value as any);
  };

  const showEditNote = (note) => {
    setEditVenueNoteOpenModal(true);
    setEditNote(note);
  };

  const showFlagNote = (note) => {
    console.log("showFlagNote", note);
    setFlagVenueNoteOpenModal(true);
    setFlagNote(note);
  };

  // Add state for the notices detail modal
  const [noticesDetailOpenModal, setNoticesDetailOpenModal] = useState(false);

  // Add debug logs
  useEffect(() => {
    if (selectedMainTab === "nearbyvenues") {
      console.log("Full Nearby Businesses Response:", nearbyBusinessesData);
      console.log("Response Type:", typeof nearbyBusinessesData);
      console.log("Response Keys:", Object.keys(nearbyBusinessesData || {}));
      if (nearbyBusinessesData?.data?.nearbyBusinessCategories) {
        console.log(
          "Categories:",
          nearbyBusinessesData.data.nearbyBusinessCategories
        );
      }
    }
  }, [selectedMainTab, nearbyBusinessesData]);

  const handleOpenFullImage = (image) => {
    setCurrentImage(image);
    setFullImageView(true);
    
    // Track opening the full image viewer
    AnalyticsService.trackGAEventHandler("openPhotoLightbox");
    AnalyticsService.trackMx("openPhotoLightbox", {
      VenueId: venueDetails.business.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueTypes: venueDetails.businessType,
      CategoryName: "Sitemap",
      SubcategoryName: "Sitemap Photo",
    });
  };

  // Add function to load image dimensions
  const loadImageDimensions = (url: string) => {
    return new Promise<{width: number, height: number}>((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        });
      };
      img.onerror = () => {
        // Fallback dimensions if image fails to load
        resolve({
          width: 800,
          height: 600
        });
      };
      img.src = url;
    });
  };

  // Load dimensions for all sitemap photos
  useEffect(() => {
    const loadAllDimensions = async () => {
      if (venueDetails.business.siteMapPhoto) {
        const photos = venueDetails.business.siteMapPhoto.split('|');
        const dimensions: {[key: string]: {width: number, height: number}} = {};
        
        for (const photo of photos) {
          const url = bucketUrlPrefix + photo;
          dimensions[photo] = await loadImageDimensions(url);
        }
        
        setPhotoDimensions(dimensions);
      }
    };

    loadAllDimensions();
  }, [venueDetails.business.siteMapPhoto]);

  // Load dimensions for transport option image
  useEffect(() => {
    const loadTransportImageDimensions = async () => {
      if (venueDetails.business.transportOptionImage) {
        const url = bucketUrlPrefix + venueDetails.business.transportOptionImage;
        const dimensions = await loadImageDimensions(url);
        setPhotoDimensions(prev => ({
          ...prev,
          [venueDetails.business.transportOptionImage]: dimensions
        }));
      }
    };

    loadTransportImageDimensions();
  }, [venueDetails.business.transportOptionImage]);

  return (
    <>
      {/* Full Size expand Link */}
      {!fullSize && (
        <ScrollToTop
          smooth
          component={
            <img
              src={
                isDark
                  ? "/images/BackToTop_Dark.svg"
                  : "/images/BackToTop_Light.svg"
              }
              alt="Scroll to top"
            />
          }
        />
      )}

      <div className={cn(fullSize ? "h-full mt-8" : "h-full mt-0")}>
        {/* Venue name and type */}
        {hasAccessibilityData && (
          <>
            <div className="text-neutral-700 dark:text-neutral-300 flex items-center gap-2 mb-1">
              <span className="paragraph-small-medium" tabIndex={0}>
                {venueDetails.businessType} | {venueDetails.distance}{" "}
                {i18n.get("milesAway")}
              </span>
            </div>
            <div className="flex flex-row justify-between mb-2">
              <h1
                className="text-primary-800 dark:text-primary-100 desktop-h4-medium-semi-bold"
                tabIndex={0}
              >
                {venueDetails.name}
              </h1>
            </div>
          </>
        )}

        {!hasAccessibilityData && !isLoadingTabs && hasNearbyVenues && (
          <>
            <div className="text-neutral-700 dark:text-neutral-300 flex items-center gap-2 mb-1">
              <span className="paragraph-small-medium" tabIndex={0}>
                {venueDetails.businessType} | {venueDetails.distance}{" "}
                {i18n.get("milesAway")}
              </span>
            </div>
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
              <h1
                className="text-primary-800 dark:text-primary-100 desktop-h4-medium-semi-bold mb-2 md:mb-0"
                tabIndex={0}
              >
                {venueDetails.name}
              </h1>
              <VenueInfoButtons venueDetailsReponse={venueDetailsReponse} />
            </div>
          </>
        )}

        {/* Description */}
        {venueDetails.business.description && (
          <section className="mb-4">
            <div className="">
              <div className="flex">
                <div className="flex-1">
                  <div>
                    <p className="paragraph-small-medium text-primary-800 dark:text-primary-50">
                      {venueDetails.business.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Top Notices Section */}
        {venueDetails.businessNotices &&
          venueDetails.businessNotices.length > 0 && (
            <NoticesTopView
              businessNotices={venueDetails.businessNotices}
              onOpenDetailModal={() => setNoticesDetailOpenModal(true)}
            />
          )}

        {/* Add the Notices Detail Modal */}
        {noticesDetailOpenModal && (
          <Modal
            focusTrapped
            center
            blockScroll={true}
            animationDuration={0}
            open={noticesDetailOpenModal}
            onClose={() => setNoticesDetailOpenModal(false)}
            classNames={{
              modal:
                "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
              closeButton: "hidden",
              overlay: "bg-[#00000099]",
            }}
          >
            <NoticesView
              notices={venueDetails.businessNotices.map(
                mapVenueNoticeToNotices
              )}
              onClose={() => setNoticesDetailOpenModal(false)}
            />
          </Modal>
        )}

        {/* Notes block comes here  */}
        {showMapperView &&
          ((venueDetails?.businessNotesForMappers?.length || 0) > 0 ? (
            <MapperNotesComponent
              data={venueDetails?.businessNotesForMappers || []}
              publicNotes={venueDetails?.businessSociabilitySaysApproved || []}
              gotoMapperView={() => {
                setSelectedMainTab("mapperNotes");
                handleTabTracking("mapperNotes");
                // Scroll to mapperNotes section
                mapperNotesRef.current?.scrollIntoView({
                  behavior: "smooth",
                });
              }}
            />
          ) : (
            <VenueNotes
              data={venueDetails?.businessSociabilitySaysApproved || []}
              gotoMapperView={() => {
                setSelectedMainTab("mapperNotes");
                handleTabTracking("mapperNotes");
                // Scroll to mapperNotes section
                mapperNotesRef.current?.scrollIntoView({
                  behavior: "smooth",
                });
              }}
            />
          ))}
        {!showMapperView && (
          <VenueNotes
            data={venueDetails?.businessSociabilitySaysApproved || []}
            gotoMapperView={() => {
              setSelectedMainTab("mapperNotes");
              handleTabTracking("mapperNotes");
              // Scroll to mapperNotes section
              mapperNotesRef.current?.scrollIntoView({
                behavior: "smooth",
              });
            }}
          />
        )}

        {hasAccessibilityData && !isLoadingTabs && (
          <>
            {/* Photo Slider */}
            <section className="mb-2">
              {venueDetails.businessPhotos.length > 0 && (
                <div className="relative">
                  <ScrollContainer className="hidden md:block">
                    <div
                      className={cn(
                        "grid [grid-template-columns:repeat(24,minmax(0,1fr))] gap-4 mb-2",
                        fullSize
                          ? "[grid-auto-rows:200px]"
                          : "[grid-auto-rows:150px]"
                      )}
                    >
                      {top5Photos.map((photo, index) => (
                        <Link
                          role="button"
                          to={`/venuephotos/${venueDetails.business.slug}`}
                          search={
                            {
                              reviewId:
                                photo.reviewPhotosId as unknown as string,
                              sort: "category",
                            } as any
                          }
                          className={cn(
                            index === 0
                              ? "col-span-10 row-span-2"
                              : "col-span-7 row-span-1"
                          )}
                          key={index.toString()}
                          target={fullSize ? "" : "_blank"}
                          onClick={() => {
                            AnalyticsService.trackGAEventHandler(
                              "viewedSectionPhotoCarousel"
                            );
                            AnalyticsService.trackMx(
                              "viewedSectionPhotoCarousel",
                              {
                                VenueId: venueDetails.id,
                                VenueBrandId: venueDetails.brandId,
                                VenueBrandName: venueDetails.brandName,
                                VenueAddress: venueDetails.address,
                                VenueName: venueDetails.name,
                                VenueTypes: venueDetails.businessType,
                                PhotoCategory: photo.tagName,
                                PhotoId: photo.reviewPhotosId,
                                IsMobile: false,
                              }
                            );
                          }}
                        >
                          <ThumbCard
                            key={index}
                            thumb={bucketUrlPrefix + photo.path}
                            value={!fullSize ? "" : photo.tagName}
                            altText={photo.altText}
                          />
                        </Link>
                      ))}
                    </div>
                  </ScrollContainer>
                  <div className="hidden md:block absolute right-0 bottom-2">
                    <Link
                      role="button"
                      to={`/venuephotos/${venueDetails.business.slug}`}
                      className="flex justify-end bg-primary-50 rounded-tl-[12px] rounded-br-[8px] flex items-center justify-center gap-2 h-10 px-3 py-3 button-small-semi-bold"
                      target={fullSize ? "" : "_blank"}
                      onClick={() => {
                        AnalyticsService.trackGAEventHandler(
                          "viewedAllPhotoCarousel"
                        );
                        AnalyticsService.trackMx("viewedAllPhotoCarousel", {
                          VenueId: venueDetails.id,
                          VenueBrandId: venueDetails.brandId,
                          VenueBrandName: venueDetails.brandName,
                          VenueAddress: venueDetails.address,
                          VenueName: venueDetails.name,
                          VenueTypes: venueDetails.businessType,
                          IsAllPhotosButton: true,
                        });
                      }}
                    >
                      <div className="w-6 h-6 relative">
                        <img
                          src="/images/PhotoFrame.svg"
                          alt=""
                          aria-hidden="true"
                        />
                      </div>
                      <span className="button-small-semi-bold text-primary-800">
                        {i18n.get("allPhotos")}
                      </span>
                    </Link>
                  </div>
                </div>
              )}
              {venueDetails.businessPhotos.length > 0 && (
                <ScrollContainer className="block md:hidden">
                  <div className="flex flex-row gap-4 mb-2">
                    {top5Photos.map((photo, index) => (
                      <Link
                        role="button"
                        to={`/venuephotos/${venueDetails.business.slug}`}
                        search={
                          {
                            reviewId: photo.reviewPhotosId as unknown as string,
                            sort: "category",
                          } as any
                        }
                        key={index.toString()}
                      >
                        <ThumbCard
                          key={index}
                          thumb={bucketUrlPrefix + photo.path}
                          value={photo.tagName}
                          className="w-[200px]"
                          altText={photo.altText}
                        />
                      </Link>
                    ))}
                  </div>
                </ScrollContainer>
              )}
            </section>
          </>
        )}

        {hasAccessibilityData && !isLoadingTabs && (
          <main className="" role="none">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {/* Venue Info */}
              <VenueInfoAddress
                venueDetailsReponse={venueDetailsReponse}
                fullSize={fullSize}
              />

              <VenueInfoGoogleMap
                venueDetailsReponse={venueDetailsReponse}
                fullSize={fullSize}
              />
            </div>
            <hr className="my-6 border-zinc-200" />
          </main>
        )}

        {/* Main Tabs area */}
        <section className="mb-10">
          {isLoadingTabs ? (
            <Loader />
          ) : (
            <>
              <div
                ref={accessibilitySectionRefs}
                className="flex flex-row space-x-4 md:space-x-6 border-b border-b-primary-150 dark:border-b-primary-300"
              >
                {mainTabs.map((e, i) => (
                  <button
                    className={cn(
                      "py-2 px-1 md:px-4 text-primary-800 dark:text-primary-150 paragraph-medium-semi-bold",
                      selectedMainTab === e &&
                        "border-b-2 border-primary-500 dark:border-primary-300 !text-primary-500 dark:!text-primary-300"
                    )}
                    key={e}
                    onClick={() => {
                      setSelectedMainTab(e as any);
                      handleTabTracking(e);
                    }}
                    role="tab"
                    aria-label={`${mainTabName[i]} ${selectedMainTab === e ? "selected" : ""} `}
                  >
                    {mainTabName[i]}
                    {e === "reviews"
                      ? ` (${venueDetails.businessReviews.length})`
                      : null}
                    {e === "mapperNotes"
                      ? ` (${
                          showMapperView
                            ? venueDetailsReponse.details
                                .businessNotesForMappers.length +
                              mergedNotes.length
                            : venueDetailsReponse.details
                                .businessSociabilitySaysApproved.length
                        })`
                      : null}
                    {showNoticesTab === "Yes" && e === "notices"
                      ? ` (${venueDetailsReponse.details.businessNotices.length})`
                      : null}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                {/* Accessibility BASIC */}
                {selectedMainTab === "accessibility" &&
                  venueDetails.templateType === "Basic" && (
                    <div>
                      <AccessibilitySection
                        venueDetails={venueDetails}
                        accessibilityCategories={accessibilityCategories}
                        fullSize={fullSize}
                        initialSection={initialSection}
                        onLogin={() => {
                          handleShowLoginModal();
                        }}
                      />
                    </div>
                  )}

                {/* Accessibility HYBRID */}
                {selectedMainTab === "accessibility" &&
                  venueDetails.templateType !== "Basic" &&
                  venueDetails.isPublicTemplate === "Yes" && (
                    <div>
                      <AccessibilitySectionHybrid
                        venueDetails={venueDetails}
                        accessibilityCategories={accesibilityCategoriesHybrid}
                        fullSize={fullSize}
                        initialSection={initialSection}
                        onLogin={() => {
                          handleShowLoginModal();
                        }}
                      />
                    </div>
                  )}

                {/* Reviews */}
                {showReviewsTab && selectedMainTab === "reviews" && (
                  <section className="mb-10">
                    <header className="flex items-center justify-between gap-4 mb-6">
                      <div className="flex flex-row border border-primary-200 bg-primary-50 dark:bg-dark-800 rounded-[12px] min-h-[52px] px-4 py-3">
                        <CirclePlus size={24} strokeWidth={"1.5"} />
                        <button
                          className="button-small-semi-bold text-primary-800 dark:text-primary-150 ml-2 truncate"
                          onClick={() => {
                            if (isLoggedIn) {
                              setIsAddReviewOpen(true);
                            } else {
                              // Set the method in the Atom
                              setActionToPerformAfterLogin("addReview");
                              // Show login modal
                              handleShowLoginModal();
                            }
                          }}
                        >
                          Add Review
                        </button>
                        <AddReviewModal
                          venueId={venueDetails.business.id.toString()}
                          venueName={venueDetails.name}
                          isOpen={isAddReviewOpen}
                          onClose={() => handleAddReviewClose()}
                          venueDetails={venueDetails}
                        />
                      </div>
                      <div>
                        {/* Sorting dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger>
                            {/* Sort list button */}
                            <div className="flex flex-row justify-center items-center min-w-[180px] py-2 bg-primary-50 dark:bg-dark-800 rounded-xl border border-primary-200 cursor-pointer">
                              <div className="flex-1 pl-4">
                                <div className="paragraph-xsmall-semibold text-left text-primary-500 dark:text-primary-150">
                                  Sort by
                                </div>
                                <div className="paragraph-small-regular text-left">
                                  {currentReviewSort}
                                </div>
                              </div>
                              <div className="w-6 h-6 relative rounded-[20px] mr-4">
                                <CaretDown size={24} />
                              </div>
                            </div>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            className="bg-primary-50 dark:bg-secondary-800 rounded-xl border border-[#cbc9f2] min-w-[200px]"
                            align="end"
                          >
                            <DropdownMenuLabel className="paragraph-xsmall-semibold text-left text-primary-500 dark:text-primary-150">
                              Sort by
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuRadioGroup
                              value={currentReviewSort}
                              onValueChange={(value) => {
                                handleReviewSort(value as any);
                              }}
                            >
                              <DropdownMenuRadioItem
                                value="Latest"
                                className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                              >
                                Latest
                              </DropdownMenuRadioItem>
                              <DropdownMenuRadioItem
                                value="Earliest"
                                className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                              >
                                Earliest
                              </DropdownMenuRadioItem>
                              <DropdownMenuRadioItem
                                value="Alphabetical"
                                className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                              >
                                Alphabetical
                              </DropdownMenuRadioItem>
                            </DropdownMenuRadioGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </header>

                    <main
                      className={cn(
                        "grid",
                        fullSize
                          ? "md:grid-cols-1 grid-cols-1 gap-6 overflow-y-auto"
                          : "md:grid-cols-1 grid-cols-1 gap-6"
                      )}
                    >
                      <BusinessReviews
                        fullSize={fullSize}
                        reviews={reviews}
                        onFlag={(review) => {
                          if (!user || !isLoggedIn) {
                            // Set the method in the Atom
                            console.log(
                              "setActionToPerformAfterLogin in the Atom"
                            );
                            setActionToPerformAfterLogin("flagReview");
                            setActionToPerformValueAfterLogin(review);

                            handleShowLoginModal();
                            return;
                          }
                          setSelectedReview(review);
                          setIsFlagReviewOpen(true);
                        }}
                      />
                    </main>
                    {venueDetails.businessReviews.length === 0 && (
                      <div className="items-center justify-center">
                        <img
                          src={NoReviews}
                          alt="No reviews"
                          className="max-w-[80%] mx-auto my-10"
                          aria-hidden="true"
                        />
                        <p
                          className="text-center desktop-h2-small-bold"
                          tabIndex={0}
                        >
                          Be the first to review this venue!
                        </p>
                        <p
                          className="text-center paragraph-small-regular mt-3"
                          tabIndex={0}
                        >
                          Your experience matters. Share your thoughts to help
                          others in the community. Tap 'Add review' above to get
                          started.
                        </p>
                      </div>
                    )}
                  </section>
                )}

                {/* Mapper & Public */}
                {showNotesTab && selectedMainTab === "mapperNotes" && (
                  <div ref={mapperNotesRef}>
                    <VenueMapperNotes
                      venueDetailsReponse={venueDetailsReponse}
                      onRefresh={onRefresh}
                      showMapperView={showMapperView}
                      onEdit={showEditNote}
                      onFlagNote={showFlagNote}
                    />
                  </div>
                )}

                {/* Notices */}
                {showNoticesTab === "Yes" && selectedMainTab === "notices" && (
                  <div className="flex flex-col">
                    <div className="mt-2 grid md:grid-cols-1 grid-cols-1 gap-2">
                      <NoticesView
                        notices={venueDetailsReponse.details.businessNotices.map(
                          mapVenueNoticeToNotices
                        )}
                      />
                    </div>
                  </div>
                )}

                {/* Nearby Venues */}
                {selectedMainTab === "nearbyvenues" && (
                  <div className="mt-4">
                    {isLoadingNearbyBusinesses || !nearbyBusinessesData ? (
                      <div className="flex flex-col items-center justify-center py-8">
                        <p className="text-center paragraph-small-regular text-primary-500 dark:text-primary-300">
                          Loading nearby venues...
                        </p>
                      </div>
                    ) : (
                      <NearbyVenues
                        categories={
                          nearbyBusinessesData?.data
                            ?.nearbyBusinessCategories || []
                        }
                        nearbyBusinessList={
                          nearbyBusinessesData?.data?.nearbyBusinessList || []
                        }
                        venueLatitude={venueDetails.latitude}
                        venueLongitude={venueDetails.longitude}
                        venueId={venueDetails.business.id.toString()}
                        nearbyFilterAccessibility={
                          nearbyBusinessesData?.data?.filter_accessibility
                        }
                        accessibilityFeaturesRequirementsHiddenIds={nearbyBusinessesData?.data?.accessibility_features_requirements_hidden_ids.map(
                          String
                        )}
                        accessibilityFeaturesRequirementsShowHiddenById={nearbyBusinessesData?.data?.accessibility_features_requirements_show_hidden_by_id.toString()}
                      />
                    )}
                  </div>
                )}

                {/* Sitemap */}
                {selectedMainTab === "sitemap" && (
                  <div className="flex flex-col">
                    <div className="mt-2">
                      <RowsPhotoAlbum
                        photos={venueDetails.business.siteMapPhoto
                          ?.split("|")
                          .map((photo, index) => {
                            const dimensions = photoDimensions[photo] || {
                              width: 200,
                              height: 200,
                            };
                            return {
                              src: bucketUrlPrefix + photo,
                              width: dimensions.width,
                              height: dimensions.height,
                              alt: `Sitemap photo ${index + 1}`,
                            };
                          })}
                        spacing={8}
                        targetRowHeight={200}
                        rowConstraints={{
                          maxPhotos: 6,
                          minPhotos: 1,
                        }}
                        onClick={({ index }) => {
                          const photos =
                            venueDetails.business.siteMapPhoto?.split("|") ||
                            [];
                          handleOpenFullImage(photos[index]);
                        }}
                      />

                      {/* Full image view */}
                      {fullImageView && currentImage && (
                        <FullImageViewer
                          images={venueDetails.business.siteMapPhoto
                            ?.split("|")
                            .map((photo, index) => ({
                              src: bucketUrlPrefix + photo,
                              alt: "Sitemap photo " + (index + 1),
                              tag_name: "Sitemap",
                              category_name: "Sitemap",
                              created_at: null,
                              photo_type: "sitemap",
                              review_photos_id: null,
                              width: photo.width,
                              height: photo.height,
                              sort_order: photo.sort_order,
                              qtags: photo.qtags || [],
                              tags: photo.tags || [
                                {
                                  tagName: photo.tag_name || "Uncategorized",
                                  categoryDisplaySectionId: "",
                                },
                              ],
                              displaySections: photo.displaySections,
                              venueId: venueDetails.business.id,
                              venueName: venueDetails.name,
                              venueAddress: venueDetails.address,
                              venueBrandId: venueDetails.brandId,
                              venueBrandName: venueDetails.brandName,
                              venueTypes: venueDetails.businessType,
                            }))}
                          currentIndex={
                            venueDetails.business.siteMapPhoto
                              ?.split("|")
                              .findIndex((p) => p === currentImage) || 0
                          }
                          onClose={() => setFullImageView(false)}
                          onFlag={() => {}}
                          showFlag={false}
                          showRightTab={false}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Transport Options */}
                {selectedMainTab === "transportOptions" && (
                  <div className="flex flex-col">
                    <div className="mt-2 grid md:grid-cols-1 grid-cols-1 gap-2">
                      <section className="mb-4">
                        <div className="">
                          <div className="flex">
                            <div className="flex-1">
                              <div>
                                <div 
                                  tabIndex={0}
                                  className="paragraph-small-medium text-primary-800 dark:text-primary-50 pb-8 transport-options-content"
                                  dangerouslySetInnerHTML={{ 
                                    __html: venueDetails.business.transportOptionText ? 
                                      venueDetails.business.transportOptionText
                                        .replace(/\r\n|\r|\n/g, '<br />') : '' 
                                  }}
                                ></div>
                               
                              </div>

                              {venueDetails.business.transportOptionImage && (
                                <div
                                  className="mt-2 mx-auto w-[100%] h-auto md:w-[50%]"
                                  onClick={() => {
                                    handleOpenFullImage(
                                      venueDetails.business.transportOptionImage
                                    );
                                  }}
                                >
                                  <div className={`h-full`}>
                                    <div className="relative z-0 overflow-hidden h-full">
                                      <img
                                        tabIndex={-1}
                                        className="w-full h-full object-cover"
                                        src={bucketUrlPrefix + venueDetails.business.transportOptionImage}
                                        alt={venueDetails.business.transportOptionText || "Transport option image"}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Full image view */}
                              {fullImageView && currentImage && (
                                <FullImageViewer
                                  images={[
                                    {
                                      src:
                                        bucketUrlPrefix +
                                        venueDetails.business
                                          .transportOptionImage,
                                      alt:
                                        venueDetails.business
                                          .transportOptionText || "",
                                      tag_name: "Transport Options",
                                      category_name: "Transport",
                                      created_at: null,
                                      photo_type: "transport",
                                      review_photos_id: null,
                                      width:
                                        photoDimensions[
                                          venueDetails.business
                                            .transportOptionImage
                                        ]?.width || 800,
                                      height:
                                        photoDimensions[
                                          venueDetails.business
                                            .transportOptionImage
                                        ]?.height || 600,
                                      sort_order: 0,
                                      qtags: [],
                                      tags: [
                                        {
                                          tagName: "Transport Options",
                                          categoryDisplaySectionId: "",
                                        },
                                      ],
                                      displaySections: {},
                                      venueId: venueDetails.business.id,
                                      venueName: venueDetails.name,
                                      venueAddress: venueDetails.address,
                                      venueBrandId: venueDetails.brandId,
                                      venueBrandName: venueDetails.brandName,
                                      venueTypes: venueDetails.businessType,
                                    },
                                  ]}
                                  currentIndex={0}
                                  onClose={() => setFullImageView(false)}
                                  onFlag={() => {}}
                                  showFlag={false}
                                  showRightTab={false}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {areaDetailsOpenModal && (
          <Modal
            focusTrapped
            center
            blockScroll={true}
            animationDuration={0}
            open={areaDetailsOpenModal}
            onClose={() => setAreaDetailsOpenModal(false)}
            classNames={{
              modal:
                "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
              closeButton: "hidden",
            }}
          >
            <AreaDetailsNotes
              venueDetailsReponse={venueDetailsReponse}
              onClose={() => {
                setAreaDetailsOpenModal(false);
              }}
              mode="mappernote"
            />
          </Modal>
        )}

        <FlagVenueReview
          review={selectedReview}
          venueName={venueDetails.name}
          isOpen={isFlagReviewOpen}
          onClose={() => handleFlagVenueReviewClose()}
          venueDetails={venueDetails}
        />
        {addNoteOpenModal && (
          <Modal
            focusTrapped
            center
            blockScroll={true}
            animationDuration={0}
            open={addNoteOpenModal}
            onClose={() => handleAddNoteModalClose()}
            classNames={{
              modal:
                "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
              closeButton: "hidden",
            }}
          >
            <AddMapperNote
              venueDetailsReponse={venueDetailsReponse}
              onClose={() => handleAddNoteModalClose()}
              onInfoClick={() => {
                setAreaDetailsOpenModal(true);
              }}
            />
          </Modal>
        )}

        {editVenueNoteOpenModal && (
          <Modal
            focusTrapped
            center
            blockScroll={true}
            animationDuration={0}
            open={editVenueNoteOpenModal}
            onClose={() => setEditVenueNoteOpenModal(false)}
            classNames={{
              modal:
                "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
              closeButton: "hidden",
            }}
          >
            <AddVenueNote
              venueDetailsReponse={venueDetailsReponse}
              onClose={() => setEditVenueNoteOpenModal(false)}
              onInfoClick={() => {
                setAreaDetailsOpenModal(true);
              }}
              note={editNote}
            />
          </Modal>
        )}

        {flagVenueNoteOpenModal && (
          <Modal
            focusTrapped
            center
            blockScroll={true}
            animationDuration={0}
            open={flagVenueNoteOpenModal}
            onClose={() => setFlagVenueNoteOpenModal(false)}
            classNames={{
              modal:
                "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
              closeButton: "hidden",
            }}
          >
            <FlagVenueNote
              venueDetails={venueDetails}
              onClose={() => setFlagVenueNoteOpenModal(false)}
              note={flagNote}
            />
          </Modal>
        )}
      </div>
    </>
  );
}

export default VenueSheetContent;
